import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { orders } from '@/utils/api';

const RazorpayCheckout = ({ product, onSuccess }) => {
  const [showDialog, setShowDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [shippingDetails, setShippingDetails] = useState({
    address: '',
    quantity: 1,
    buyerGstNumber: ''
  });

  const user = JSON.parse(localStorage.getItem('user') || 'null');

  const handleBuyNow = () => {
    if (!user) {
      toast.error('Please login to purchase');
      return;
    }
    
    if (user.userType !== 'buyer') {
      toast.error('Only buyers can purchase products');
      return;
    }
    
    setShowDialog(true);
  };

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handleProceedToPayment = async () => {
    if (!shippingDetails.address.trim()) {
      toast.error('Please enter shipping address');
      return;
    }

    if (shippingDetails.quantity < 1) {
      toast.error('Quantity must be at least 1');
      return;
    }

    setLoading(true);

    try {
      // Load Razorpay script
      const res = await loadRazorpayScript();
      if (!res) {
        toast.error('Razorpay SDK failed to load');
        setLoading(false);
        return;
      }

      // Create order on backend
      const orderResponse = await orders.create({
        productId: product.id,
        quantity: shippingDetails.quantity,
        shippingAddress: shippingDetails.address,
        buyerGstNumber: shippingDetails.buyerGstNumber || null
      });

      const { order, razorpayOrderId, razorpayKeyId, commissionInfo } = orderResponse.data;

      // Calculate display amounts
      const gstAmount = order.gst;
      const totalAmount = order.totalAmount;

      // Razorpay options
      const options = {
        key: razorpayKeyId,
        amount: order.totalAmount * 100, // Amount in paise
        currency: 'INR',
        name: 'ARTCARDO',
        description: `Purchase: ${product.title}`,
        order_id: razorpayOrderId,
        handler: async function (response) {
          try {
            // Verify payment on backend
            await orders.verifyPayment({
              orderId: order.id,
              razorpayPaymentId: response.razorpay_payment_id,
              razorpayOrderId: response.razorpay_order_id,
              razorpaySignature: response.razorpay_signature
            });

            toast.success('Payment successful! Order confirmed.');
            setShowDialog(false);
            if (onSuccess) onSuccess();
          } catch (error) {
            toast.error('Payment verification failed');
            console.error('Verification error:', error);
          }
        },
        prefill: {
          name: user.name,
          email: user.email,
          contact: user.phone || ''
        },
        notes: {
          productId: product.id,
          productTitle: product.title,
          commissionRate: commissionInfo?.commissionRate || '0%',
          commissionAmount: commissionInfo?.commissionAmount || 0
        },
        theme: {
          color: '#ef4444'
        }
      };

      const paymentObject = new window.Razorpay(options);
      paymentObject.on('payment.failed', function (response) {
        toast.error('Payment failed: ' + response.error.description);
        console.error('Payment failed:', response.error);
      });

      paymentObject.open();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create order');
      console.error('Order creation error:', error);
    } finally {
      setLoading(false);
    }
  };

  const gstAmount = product.price * (product.gstRate / 100);
  const totalPrice = product.price + gstAmount;

  return (
    <>
      <Button
        onClick={handleBuyNow}
        className="w-full bg-[#ef4444] hover:bg-[#dc2626] text-white text-lg py-6"
        size="lg"
      >
        Buy Now - Pay Securely
      </Button>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Complete Your Purchase</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Product Summary */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-semibold mb-2">{product.title}</h3>
              <p className="text-sm text-gray-600">by {product.artistName}</p>
              
              <div className="mt-3 space-y-1 text-sm">
                <div className="flex justify-between">
                  <span>Base Price:</span>
                  <span>₹{product.price.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span>GST ({product.gstRate}%):</span>
                  <span>₹{gstAmount.toLocaleString()}</span>
                </div>
                <div className="flex justify-between font-bold text-base pt-2 border-t">
                  <span>Total Amount:</span>
                  <span>₹{totalPrice.toLocaleString()}</span>
                </div>
              </div>

              <p className="text-xs text-green-600 mt-2">
                100% Secure Payment via Razorpay
              </p>
            </div>

            {/* Quantity */}
            <div>
              <label className="block text-sm font-medium mb-2">Quantity</label>
              <select
                value={shippingDetails.quantity}
                onChange={(e) => setShippingDetails({ ...shippingDetails, quantity: parseInt(e.target.value) })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
              >
                {[...Array(Math.min(10, product.stock))].map((_, i) => (
                  <option key={i + 1} value={i + 1}>
                    {i + 1}
                  </option>
                ))}
              </select>
            </div>

            {/* Shipping Address */}
            <div>
              <label className="block text-sm font-medium mb-2">Shipping Address *</label>
              <Textarea
                placeholder="Enter your complete shipping address"
                value={shippingDetails.address}
                onChange={(e) => setShippingDetails({ ...shippingDetails, address: e.target.value })}
                rows={3}
                required
              />
            </div>

            {/* Buyer GST Number (Optional) */}
            <div>
              <label className="block text-sm font-medium mb-2">GST Number (Optional)</label>
              <Input
                type="text"
                placeholder="Enter your GSTIN (e.g., 29XXXXX1234X1ZX)"
                value={shippingDetails.buyerGstNumber}
                onChange={(e) => setShippingDetails({ ...shippingDetails, buyerGstNumber: e.target.value.toUpperCase() })}
                maxLength={15}
              />
              <p className="text-xs text-gray-500 mt-1">
                Provide your GST number if you need a GST invoice for business purposes
              </p>
            </div>

            {/* Payment Button */}
            <Button
              onClick={handleProceedToPayment}
              disabled={loading}
              className="w-full bg-[#ef4444] hover:bg-[#dc2626]"
              size="lg"
            >
              {loading ? 'Processing...' : `Pay ₹${(totalPrice * shippingDetails.quantity).toLocaleString()} Securely`}
            </Button>

            <p className="text-xs text-center text-gray-500">
              Payment powered by Razorpay • 100% Secure • Multiple payment options
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default RazorpayCheckout;
